import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { User, MapPin, Calendar, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function MyTerco() {
  const { t, lang } = useI18n();
  const { user, updateProfile } = useAuth();
  const [dash, setDash] = useState({ community: null, events: [] });
  const [profile, setProfile] = useState({ name: "", city: "", country: "" });

  useEffect(() => {
    if (user) {
      setProfile({ name: user.name || "", city: user.city || "", country: user.country || "" });
      api.get("/me/dashboard").then((r) => setDash(r.data)).catch(() => {});
    }
  }, [user]);

  const save = async (e) => {
    e.preventDefault();
    await updateProfile(profile);
    toast.success(t("saved"));
  };

  if (!user) return null;

  return (
    <div>
      <section className="bg-th-blue th-pattern text-white py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-th-gold flex items-center justify-center text-th-bluedark font-display font-bold text-2xl">{(user.name || "?")[0]}</div>
          <div>
            <div className="text-th-gold text-xs uppercase tracking-widest">{t("myterco_welcome")}</div>
            <h1 className="font-display font-bold text-3xl">{user.name}</h1>
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid md:grid-cols-2 gap-8">
        <div className="space-y-8">
          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
            <h2 className="font-display font-semibold text-lg text-th-blue flex items-center gap-2 mb-4"><MapPin className="w-5 h-5 text-th-gold" />{t("myterco_community")}</h2>
            {dash.community ? (
              <Link to={`/comunidades/${dash.community.id}`} className="block hover:text-th-gold" data-testid="myterco-community">
                <div className="font-medium text-th-blue">{dash.community.name}</div>
                <div className="text-sm text-slate-500">{dash.community.parish}, {dash.community.city} · {dash.community.schedule}</div>
              </Link>
            ) : <p className="text-slate-500 text-sm" data-testid="myterco-no-community">{t("myterco_no_community")}</p>}
          </div>

          <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm">
            <h2 className="font-display font-semibold text-lg text-th-blue flex items-center gap-2 mb-4"><Calendar className="w-5 h-5 text-th-gold" />{t("myterco_events")}</h2>
            <div className="space-y-2" data-testid="myterco-events">
              {dash.events.length === 0 && <p className="text-slate-500 text-sm">{t("myterco_no_events")}</p>}
              {dash.events.map((e) => (
                <Link to={`/eventos/${e.id}`} key={e.id} className="block border border-slate-100 rounded-lg p-3 hover:border-th-gold">
                  <div className="text-xs text-th-gold font-semibold">{new Date(e.date).toLocaleDateString(lang)}</div>
                  <div className="font-medium text-th-blue text-sm">{e.title}</div>
                </Link>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm h-fit">
          <h2 className="font-display font-semibold text-lg text-th-blue flex items-center gap-2 mb-4"><User className="w-5 h-5 text-th-gold" />{t("myterco_profile")}</h2>
          <form onSubmit={save} className="space-y-4" data-testid="profile-form">
            <div><Label>{t("form_name")}</Label><Input value={profile.name} data-testid="profile-name" onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="mt-1" /></div>
            <div><Label>{t("form_email")}</Label><Input value={user.email} disabled className="mt-1 bg-slate-50" /></div>
            <div><Label>{t("form_city")}</Label><Input value={profile.city} data-testid="profile-city" onChange={(e) => setProfile({ ...profile, city: e.target.value })} className="mt-1" /></div>
            <div><Label>{t("form_country")}</Label><Input value={profile.country} data-testid="profile-country" onChange={(e) => setProfile({ ...profile, country: e.target.value })} className="mt-1" /></div>
            <Button type="submit" data-testid="profile-save" className="w-full bg-th-blue text-white hover:bg-th-bluelight"><Save className="w-4 h-4 mr-2" />{t("save")}</Button>
          </form>
        </div>
      </section>
    </div>
  );
}
